import { motion } from "framer-motion";
import { LuArrowRight } from "react-icons/lu";
import { useLang } from "../i18n";
import { howItWorksContent } from "../data/content";
import { useDemoModal } from "../context/DemoModalContext";
import "./HowItWorks.css";

/**
 * "Såhär funkar det" — stegvis timeline (1→4). Varje steg har en egen SVG-
 * illustration som sammanfattar momentet visuellt; siffran behålls som en
 * subtil index-etikett under bilden.
 */
export default function HowItWorks() {
  const { t } = useLang();
  const { open: openDemoModal } = useDemoModal();

  return (
    <section id="how" className="how">
      <div className="container">
        <motion.header
          className="how-header"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-10%" }}
          transition={{ duration: 0.5 }}
        >
          <span className="section-eyebrow">{t(howItWorksContent.eyebrow)}</span>
          <h2 className="section-heading">{t(howItWorksContent.title)}</h2>
        </motion.header>

        <ol className="how-steps">
          {howItWorksContent.steps.map((s, idx) => (
            <motion.li
              key={s.number}
              className="how-step"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10%" }}
              transition={{ duration: 0.5, delay: idx * 0.08 }}
            >
              <div className="how-step-art" aria-hidden>
                <StepIllustration step={idx + 1} />
              </div>
              <div className="how-step-index">
                {t({ sv: "Steg", en: "Step" })} {s.number}
              </div>
              <h3>{t(s.title)}</h3>
              <p>{t(s.body)}</p>
            </motion.li>
          ))}
        </ol>

        <motion.div
          className="how-cta-row"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <button
            type="button"
            className="btn btn-primary"
            onClick={openDemoModal}
          >
            {t({ sv: "Boka en demo", en: "Book a demo" })}
            <LuArrowRight size={18} aria-hidden />
          </button>
        </motion.div>
      </div>
    </section>
  );
}

/**
 * Per-step inline SVG illustrations. Same brand-purple palette and 120×120
 * viewBox so each card has a balanced visual language.
 */
function StepIllustration({ step }: { step: number }) {
  switch (step) {
    case 1:
      return (
        <svg viewBox="0 0 120 120" role="img">
          <defs>
            <linearGradient id="hi-g1" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#A855F7" />
              <stop offset="100%" stopColor="#7C3AED" />
            </linearGradient>
          </defs>
          <circle cx="60" cy="60" r="48" fill="none" stroke="url(#hi-g1)" strokeWidth="1.25" opacity="0.18" />
          <circle cx="60" cy="60" r="36" fill="none" stroke="url(#hi-g1)" strokeWidth="1.25" opacity="0.32" />
          <circle cx="60" cy="60" r="24" fill="none" stroke="url(#hi-g1)" strokeWidth="1.5" opacity="0.55" />
          <circle cx="60" cy="60" r="16" fill="url(#hi-g1)" />
          <rect x="56.5" y="50" width="7" height="20" rx="1.5" fill="white" />
          <rect x="50" y="56.5" width="20" height="7" rx="1.5" fill="white" />
          <circle cx="92" cy="34" r="6" fill="#FB7185" />
          <circle cx="92" cy="34" r="6" fill="none" stroke="#FB7185" strokeWidth="1.5" opacity="0.4" transform="scale(1.6)" transformOrigin="92 34" />
        </svg>
      );
    case 2:
      return (
        <svg viewBox="0 0 120 120" role="img">
          <defs>
            <linearGradient id="hi-g2" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#FAF5FF" />
              <stop offset="100%" stopColor="#F3E8FF" />
            </linearGradient>
          </defs>
          <g transform="translate(62 60) rotate(-8)">
            <rect x="-26" y="-34" width="52" height="68" rx="6" fill="white" stroke="#E9D5FF" strokeWidth="1.5" />
          </g>
          <g transform="translate(58 60) rotate(4)">
            <rect x="-26" y="-34" width="52" height="68" rx="6" fill="url(#hi-g2)" stroke="#D8B4FE" strokeWidth="1.5" />
          </g>
          <g transform="translate(60 62)">
            <rect x="-26" y="-34" width="52" height="68" rx="6" fill="white" stroke="#A855F7" strokeWidth="1.6" />
            <rect x="-18" y="-22" width="28" height="3" rx="1.5" fill="#7C3AED" />
            <rect x="-18" y="-13" width="36" height="2.5" rx="1.25" fill="#A855F7" opacity="0.55" />
            <rect x="-18" y="-5" width="36" height="2.5" rx="1.25" fill="#A855F7" opacity="0.55" />
            <rect x="-18" y="3" width="24" height="2.5" rx="1.25" fill="#A855F7" opacity="0.55" />
            <rect x="-18" y="11" width="32" height="2.5" rx="1.25" fill="#A855F7" opacity="0.55" />
            <rect x="-18" y="19" width="20" height="2.5" rx="1.25" fill="#A855F7" opacity="0.55" />
          </g>
          <g transform="translate(86 36) rotate(35)">
            <rect x="-1.4" y="-12" width="2.8" height="20" rx="1.4" fill="#7C3AED" />
            <path d="M-1.4 8 L1.4 8 L0 12 Z" fill="#7C3AED" />
            <rect x="-1.4" y="-14" width="2.8" height="3" fill="#FACC15" />
          </g>
        </svg>
      );
    case 3:
      return (
        <svg viewBox="0 0 120 120" role="img">
          <defs>
            <linearGradient id="hi-g3" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#A855F7" />
              <stop offset="100%" stopColor="#7C3AED" />
            </linearGradient>
          </defs>
          <line x1="22" y1="28" x2="60" y2="60" stroke="#D8B4FE" strokeWidth="1.5" />
          <line x1="98" y1="32" x2="60" y2="60" stroke="#D8B4FE" strokeWidth="1.5" />
          <line x1="22" y1="92" x2="60" y2="60" stroke="#D8B4FE" strokeWidth="1.5" />
          <line x1="98" y1="92" x2="60" y2="60" stroke="#D8B4FE" strokeWidth="1.5" />
          <line x1="60" y1="14" x2="60" y2="42" stroke="#D8B4FE" strokeWidth="1.5" />
          <circle cx="22" cy="28" r="6" fill="#FAF5FF" stroke="#A855F7" strokeWidth="1.5" />
          <circle cx="98" cy="32" r="6" fill="#FAF5FF" stroke="#A855F7" strokeWidth="1.5" />
          <circle cx="22" cy="92" r="6" fill="#FAF5FF" stroke="#A855F7" strokeWidth="1.5" />
          <circle cx="98" cy="92" r="6" fill="#FAF5FF" stroke="#A855F7" strokeWidth="1.5" />
          <circle cx="60" cy="14" r="5" fill="#FAF5FF" stroke="#A855F7" strokeWidth="1.5" />
          <circle cx="60" cy="60" r="22" fill="url(#hi-g3)" />
          <circle cx="60" cy="60" r="22" fill="none" stroke="white" strokeWidth="1" opacity="0.25" />
          <path d="M60 46 L63.5 56.5 L74 60 L63.5 63.5 L60 74 L56.5 63.5 L46 60 L56.5 56.5 Z" fill="white" />
        </svg>
      );
    case 4:
      return (
        <svg viewBox="0 0 120 120" role="img">
          <defs>
            <linearGradient id="hi-g4" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#A855F7" />
              <stop offset="100%" stopColor="#7C3AED" />
            </linearGradient>
          </defs>
          <line x1="20" y1="92" x2="100" y2="92" stroke="#E9D5FF" strokeWidth="1.5" strokeLinecap="round" />
          <rect x="26" y="74" width="11" height="18" rx="2" fill="#E9D5FF" />
          <rect x="42" y="62" width="11" height="30" rx="2" fill="#D8B4FE" />
          <rect x="58" y="50" width="11" height="42" rx="2" fill="#A855F7" />
          <rect x="74" y="34" width="11" height="58" rx="2" fill="url(#hi-g4)" />
          <path
            d="M30 80 L48 64 L64 56 L86 32"
            stroke="#7C3AED"
            strokeWidth="2"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <circle cx="30" cy="80" r="2.5" fill="#7C3AED" />
          <circle cx="48" cy="64" r="2.5" fill="#7C3AED" />
          <circle cx="64" cy="56" r="2.5" fill="#7C3AED" />
          <circle cx="92" cy="28" r="11" fill="#7C3AED" />
          <circle cx="92" cy="28" r="11" fill="none" stroke="white" strokeWidth="1" opacity="0.35" />
          <path
            d="M86.5 28 L91 32.5 L98 24"
            stroke="white"
            strokeWidth="2.2"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    default:
      return null;
  }
}
